import {
  FieldRichText,
  FieldText,
  ModalBody,
  ModalFoot,
  ModalHead,
} from '@dixa/lib-shared-habla';
import { boundMethod } from 'autobind-decorator';
import _countBy from 'lodash/countBy';
import _find from 'lodash/find';
import _forEach from 'lodash/forEach';
import _transform from 'lodash/transform';
import _uniqueId from 'lodash/uniqueId';
import { ChangeEvent, Component, ReactElement } from 'react';
import { connect } from 'react-redux';
import {
  Field,
  FieldArray,
  FieldArrayFieldsProps,
  FieldArrayMetaProps,
  Form,
  FormErrors,
  InjectedFormProps,
  reduxForm,
} from 'redux-form';

import type { RootReducerStateT } from 'app/reducers/rootReducer';
import { FlowNodeTypes } from 'components/powerFlowShared/components/types';
import { getConfirmButtonVerb } from 'components/powerFlowShared/helpers/getConfirmButtonVerb';
import type {
  ActionNodeT,
  FlowManagerEditModalComponentT,
} from 'fable/flowEditor';

import { WidgetPreview } from '../../../conversation/components/widgetPreview';
import { getRequestID } from '../../../shared/util/request';
import type { ChatMenuModalStatePropsT } from '../../containers/modals/chatmenu';
import type { ButtonsT, ButtonT, FlowChatNodeChatMenuT } from '../nodeTypes';

type PropsT = FlowManagerEditModalComponentT<
  FlowChatNodeChatMenuT | ActionNodeT
> &
  ChatMenuModalStatePropsT &
  ChatMenuModalFormStatePropsT;

type InjectedProps = InjectedFormProps<ChatMenuModalFormValuesT, PropsT>;

export type ChatMenuModalFormValuesT = {
  buttons: ButtonT[];
  text: string;
};

type FormErrorTypeT = {
  _error: string;
};

type ValidationT = {
  buttons?: FormErrorTypeT | ButtonTypeT[];
  text?: FormErrorTypeT;
};

type ButtonTypeT = {
  button: string;
};

type ButtonResultT = {
  btn: ButtonT;
  key: string;
  newLabel?: string;
  oldLabel?: string;
};

type ButtonDifferenceT = {
  ADDED: ButtonResultT[];
  RENAMED: ButtonResultRenamedT[];
  REMOVED: ButtonResultT[];
};

type ButtonResultRenamedT = {
  newLabel: string;
  oldLabel: string;
} & ButtonResultT;

type ListButtonsPropT = {
  fields: FieldArrayFieldsProps<Partial<ButtonT>>;
  meta: FieldArrayMetaProps & { touched: boolean };
};

type ChatMenuModalFormStatePropsT = {
  buttonsValue: ButtonT[];
  messageValue: string;
};

type StateT = {
  initialText: string;
  text: string;
  hasChanges: boolean;
};

const FORM_NAME = 'chatFlowMenuForm';

const buttonDifference = (
  newButtons: ButtonsT,
  oldButtons: ButtonT[],
): ButtonDifferenceT => {
  const REMOVED: ButtonResultT[] = _transform(
    oldButtons,
    (result: ButtonResultT[], oldBtn: ButtonT) => {
      const BTN_FOUND_IN_NEW: undefined | ButtonT = _find(
        newButtons,
        (newBtn: ButtonT) => oldBtn.nodeId === newBtn.nodeId,
      );
      if (!BTN_FOUND_IN_NEW) {
        result.push({
          btn: oldBtn,
          key: _uniqueId(),
        });
      }
    },
    [],
  );

  return _transform<ButtonT, ButtonDifferenceT>(
    newButtons,
    // eslint-disable-next-line @typescript-eslint/ban-ts-comment
    // @ts-ignore
    (result: ButtonDifferenceT, newBtn: ButtonT) => {
      const BTN_FOUND_IN_OLD: undefined | ButtonT = _find(
        oldButtons,
        (oldBtn: ButtonT) => oldBtn.nodeId === newBtn.nodeId,
      );
      if (BTN_FOUND_IN_OLD) {
        if (newBtn.button !== BTN_FOUND_IN_OLD.button) {
          result.RENAMED.push({
            btn: newBtn,
            key: _uniqueId(),
            newLabel: newBtn.button,
            oldLabel: BTN_FOUND_IN_OLD.button,
          });
        }
      }

      if (!BTN_FOUND_IN_OLD) {
        result.ADDED.push({
          btn: newBtn,
          key: _uniqueId(),
        });
      }
    },
    { ADDED: [], REMOVED, RENAMED: [] },
  );
};

const validate = (values: ChatMenuModalFormValuesT): FormErrors => {
  const errors: ValidationT = {};
  if (!values.buttons || !values.buttons.length) {
    errors.buttons = {
      _error:
        'At least one button option must be entered - click on "Add option"',
    };
  } else {
    const btnsArrayErrors: ButtonTypeT[] = [];
    values.buttons.forEach((button: { button: string }, index: number) => {
      if (!button || !button.button) {
        btnsArrayErrors[index] = { button: 'Required' };
      }
    });

    const counts: Record<string, number> = _countBy(
      values.buttons,
      (btn: ButtonT) => btn.button,
    );
    let index = 0;
    _forEach(counts, (count: number) => {
      if (count > 1) {
        btnsArrayErrors[index] = { button: "You can't have duplicate options" };
      }
      index++;
    });

    if (btnsArrayErrors.length) {
      errors.buttons = btnsArrayErrors;
    }
  }

  if (!values.text || !values.text.trim().length) {
    errors.text = { _error: 'Please write a message' };
  }

  return errors;
};

const ListButtons = ({
  fields,
  meta: { error, touched, submitFailed },
}: ListButtonsPropT): ReactElement => {
  return (
    <div>
      {(touched || submitFailed) && error && <span>{error}</span>}
      {fields.map((option: string, index: number) => {
        return (
          <div
            key={`option-${index}`}
            className="input-field-option conversational-modal__option"
          >
            <Field
              name={`${option}.button`}
              isRequired={true}
              required
              type="option"
              placeholder={`Eg. "Sales" or "Finance/Billing"`}
              component={FieldText}
              label={`Option #${index + 1}`}
              suffix={{
                type: 'button',
                button: {
                  icon: 'trash',
                  onClick: () => fields.remove(index),
                },
              }}
            />
          </div>
        );
      })}
      <button
        type="button"
        className="btn btn-primary btn-block outlined"
        disabled={fields.length >= 12}
        onClick={() => fields.push({})}
      >
        Add option
      </button>
    </div>
  );
};

class ChatMenuModal extends Component<PropsT & InjectedProps, StateT> {
  constructor(props: PropsT & InjectedProps) {
    super(props);

    this.state = {
      hasChanges: false,
      initialText: props.payload ? props.payload.text : '',
      text: '',
    };
  }

  @boundMethod
  handleSaveClick(values: ChatMenuModalFormValuesT): void {
    let nodeState;
    const newButtons: ButtonsT = {};

    // @FIX - if fixing logic here, UrlTriggerModal will probably need the same update
    const oldButtons: undefined | ButtonT[] = this.props.payload?.buttons;
    const formButtons = values.buttons;

    let count = 0;
    _forEach(formButtons, (formBtn: ButtonT) => {
      newButtons[formBtn.button] = {
        ...formBtn,
        nodeId: formBtn.nodeId || getRequestID(),
        order: count,
      };
      count += 1;
    });

    if (oldButtons) {
      const diff: ButtonDifferenceT = buttonDifference(newButtons, oldButtons);

      diff.REMOVED.forEach(({ btn: { button: btnLabel } }: ButtonResultT) => {
        nodeState = this.props.updateConnectionLabel(
          nodeState,
          this.props.node,
          btnLabel,
          '',
        );
        delete newButtons[btnLabel];
      });
      diff.RENAMED.forEach(({ key, oldLabel }: ButtonResultRenamedT) => {
        nodeState = this.props.updateConnectionLabel(
          nodeState,
          this.props.node,
          oldLabel,
          key,
        );
      });
      diff.RENAMED.forEach(({ key, newLabel }: ButtonResultRenamedT) => {
        nodeState = this.props.updateConnectionLabel(
          nodeState,
          this.props.node,
          key,
          newLabel,
        );
      });
    }

    const nodeOptions: FlowChatNodeChatMenuT = {
      ...this.props.node,
      nodeType: FlowNodeTypes.CHAT_MENU,
      text: 'Menu',
      icon: 'i-channels',
      isRemovable: true,
      isEditable: true,
      isAction: false,
      overlay: [],
      badges: [],
      payload: {
        buttons: newButtons,
        text: values.text,
      },
    };

    this.props.replaceNode(nodeOptions, nodeState);
    this.props.triggerClose();
  }

  @boundMethod
  handleMessageChange(_event: ChangeEvent, newText: string) {
    this.setState({
      text: newText,
      hasChanges: true,
    });
  }

  @boundMethod
  handleCancel(): void {
    this.props.triggerClose();
  }

  getTextForPreview(): string {
    const {
      node: { nodeType },
      messageValue,
    } = this.props;
    const { initialText } = this.state;
    if (nodeType !== FlowNodeTypes.ACTION && initialText === '') {
      return 'Please select department';
    }
    return messageValue;
  }

  render() {
    const {
      node,
      nodeLabel,
      buttonsValue,
      handleSubmit,
      dirty: isChanged,
    } = this.props;

    return (
      <div className="conversational-modal modal-chatflow-holder">
        <Form onSubmit={handleSubmit(this.handleSaveClick)}>
          <ModalHead title={nodeLabel} onClose={this.handleCancel}>
            The &quot;Menu&quot; action allows you to automate quick inquiries
            and gain more context before handing complex ones over to your team.
          </ModalHead>
          <ModalBody>
            <div className="conversational-modal__columns">
              <div className="conversational-modal__column">
                <WidgetPreview
                  options={buttonsValue.map((val) => val.button)}
                  message={this.getTextForPreview()}
                  className="conversational-modal__preview"
                />
              </div>
              <div className="conversational-modal__column">
                <Field
                  component={FieldRichText}
                  name="text"
                  required
                  className="conversational-modal__editor"
                  label="Your message"
                />
                <FieldArray name="buttons" component={ListButtons} />
              </div>
            </div>
          </ModalBody>
          <ModalFoot
            confirmOnClick={handleSubmit(this.handleSaveClick)}
            confirmName={getConfirmButtonVerb(node)}
            confirmProps={{ disabled: !isChanged }}
            cancelOnClick={this.handleCancel}
          />
        </Form>
      </div>
    );
  }
}

const ChatMenuFlowForm = reduxForm<ChatMenuModalFormValuesT, PropsT>({
  form: FORM_NAME, // A unique identifier for this form.
  enableReinitialize: true,
  validate,
})(ChatMenuModal);

export default connect(
  (state: RootReducerStateT): ChatMenuModalFormStatePropsT => {
    const form = state.form[FORM_NAME];

    return {
      buttonsValue: form?.values ? form.values.buttons : [],
      messageValue: form?.values ? form.values.text : '',
    };
  },
)(ChatMenuFlowForm);
